import React from 'react'
import clsx from 'clsx'
import { MentionsInput as RMInput, Mention } from 'react-mentions'
import { useTheme } from '@mui/material'

import AreYouSureModal from '../ChatModals/AreYouSureModal'

import { TYPES } from '@SonarCX/slices/chatBoxSlice'
import useDelayedUserSearch, {
  EXCLUDE_ME
} from '@SonarCX/hooks/useDelayedUserSearch'
import { useEnvironment } from '@SonarCX/providers/EnvironmentProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import useStyles, { inputStyle } from './styles'

const SEARCHED = new Set()

const cleanData = ({ id, display }) => ({
  id: id ?? '',
  display: display ?? ''
})

const MENTION_LIMIT = 10

const MentionInput = ({
  value = '',
  onChange,
  users = [],
  className,
  singleLine = false,
  placeholder,
  onKeyDown = e => {},
  disabled = false,
  autoFocus,
  tags = [],
  inputClass,
  isP2P,
  focus,
  type,
  checkAdd = true,
  disableTags = false,
  disableAgents = false,
  inputProps = {},
  noPortalHost = false,
  onMentionAdd = () => {},
  onMentionCancel = () => {}
}) => {
  const theme = useTheme()

  const { user: currentUser } = useEnvironment()
  const { getUsers, updateUsers } = useUserAPI()

  const { search, results } = useDelayedUserSearch(
    false,
    EXCLUDE_ME(() => currentUser?.id)
  )
  React.useEffect(() => {
    updateUsers(results)
  }, [updateUsers, results])

  const mappedTags = React.useMemo(
    () => tags.map(tag => ({ id: tag, display: tag })).map(cleanData),
    [tags]
  )
  const mappedAgents = React.useMemo(() => {
    const allAgents = Object.values(getUsers(false))
    const otherAgents = allAgents.filter(a => a.id !== currentUser?.id)
    const mapped = otherAgents.map(a => ({ id: a.id, display: a.name }))
    return mapped
  }, [getUsers, currentUser])

  const [modalOpen, setModalOpen] = React.useState(false)
  const [limit, setLimit] = React.useState(false)
  const mentionedRef = React.useRef(new Set())
  const [lastID, setLastID] = React.useState(null)
  const [reason, setReason] = React.useState(null)
  const inputRef = React.useRef()
  const [focused, setFocused] = React.useState(false)
  const containerRef = React.useRef()

  const classes = useStyles()

  const displayTransform = isAgent => id => {
    const list = isAgent ? mappedAgents : mappedTags
    return ` ${list.find(val => val.id === id)?.display ?? 'Loading...'} `
  }

  const handleAgentAdd = id => {
    setLastID(id)
    onMentionAdd()
    const mentions = mentionedRef.current
    const isNew = !mentions.has(id)
    if (!isNew) {
      return
    }

    let showModal = false
    if (mentions.size === MENTION_LIMIT) {
      setLimit(true)
      showModal = true
    }
    if (checkAdd && !users.includes(id)) {
      showModal = true
    }

    if (showModal) {
      const person =
        mappedAgents.find(agent => agent.id === id)?.display || 'this person'
      const reason = `Are you sure you want to mention ${person}?`
      setReason(reason)
      setModalOpen(true)
    }
  }

  const handleClose = () => {
    setLastID(null)
    setReason(null)
    setModalOpen(false)
  }

  const handleClear = () => {
    mentionedRef.current.delete(lastID)

    // Remove old value
    const newValue = value.replace(`|@${lastID}| `, '')
    onChange({ target: { value: newValue } })

    // Reset state
    handleClose()
    onMentionCancel()
  }

  const lookupAgents = (s, cb) => {
    // Already searched or search not valid, perform local filter on agents
    if (SEARCHED.has(s) || s.length < 3) {
      const results = mappedAgents.filter(ma => {
        const search = s.toLocaleLowerCase()
        const name = ma.display.toLocaleLowerCase()
        const isMe = ma.id === currentUser?.id
        return name.includes(search) && !isMe
      })
      cb(results)
      return
    }

    SEARCHED.add(s)
    search(s, res => {
      const mapped = res.map(a => ({ id: a.id, display: a.name }))
      cb(mapped)
    })
  }

  // Need to blur to prevent doubling bug
  React.useEffect(() => {
    if (!modalOpen) {
      return
    }

    if (inputRef.current) {
      inputRef.current.blur()
    }
  }, [modalOpen])

  React.useEffect(() => {
    if (['Email', 'Task', TYPES.EMAIL].includes(type)) {
      return
    }

    if (focus) {
      if (inputRef.current && !focused) {
        inputRef.current.focus()
      }
    }
  }, [focus, type, focused])

  React.useEffect(() => {
    if (focus) {
      setFocused(false)
    }
  }, [focus])

  // Clears mentioned set when field is empty (after send)
  React.useEffect(() => {
    if (value.length === 0) {
      mentionedRef.current.clear()
    }
  }, [value.length])

  React.useEffect(() => {
    const re =
      // eslint-disable-next-line
      /\|\@[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\|/g
    const matches = [...value.matchAll(re)]

    // Reset ids
    mentionedRef.current.clear()
    matches.forEach(m => {
      const match = m[0]
      const id = match.slice(2, match.length - 1)
      mentionedRef.current.add(id)
    })
  }, [value])

  return (
    <>
      <div ref={containerRef} className={clsx(classes.root, className)}>
        <RMInput
          id='chat-input'
          inputRef={inputRef}
          value={value}
          onChange={onChange}
          allowSuggestionsAboveCursor
          allowSpaceInQuery
          autoFocus={autoFocus}
          singleLine={singleLine}
          placeholder={placeholder}
          disabled={disabled}
          suggestionsPortalHost={
            noPortalHost ? undefined : containerRef.current
          }
          onKeyDown={onKeyDown}
          style={inputStyle(theme)}
          onClick={e => e.stopPropagation()}
          className={inputClass}
          onFocus={() => setFocused(true)}
          {...inputProps}
        >
          <Mention
            data={disableAgents ? [] : lookupAgents}
            markup='|@__id__|'
            appendSpaceOnAdd={true}
            displayTransform={displayTransform(true)}
            onAdd={(...args) => {
              const index = args[2]
              const count = args[3]
              const start = value.slice(0, index)
              const end = value.slice(index + count)
              onChange({ target: { value: `${start}|@${args[0]}| ${end}` } })

              // Annoying hack to move the cursor to the new end position
              const input = inputRef.current
              if (input) {
                input.blur()
                input.focus()
              }

              handleAgentAdd(...args)
            }}
            className={classes.mention}
          />

          <Mention
            data={disableTags ? [] : mappedTags}
            markup='#(__id__)'
            appendSpaceOnAdd={true}
            displayTransform={displayTransform(false)}
            className={classes.tagMention}
            trigger='#'
          />
        </RMInput>
      </div>

      {modalOpen && (
        <AreYouSureModal
          open={modalOpen}
          onAccept={() => {
            if (limit) {
              handleClear()
              setLimit(false)
            } else {
              mentionedRef.current.add(lastID)
              handleClose()
            }
          }}
          onClose={() => {
            if (!limit) {
              handleClear()
            }
          }}
          title={limit ? 'ALERT' : null}
          reason={
            limit
              ? `Only ${MENTION_LIMIT} unique user${
                  MENTION_LIMIT === 1 ? '' : 's'
                } can be mentioned per message`
              : reason
          }
          subtext={
            limit
              ? null
              : isP2P
              ? 'They will not be able to see this mention or any messages'
              : 'Doing so will add them to this conversation'
          }
          hideCancel={limit || isP2P}
        />
      )}
    </>
  )
}

export default MentionInput
